import React, { useEffect, useRef } from "react";

const DEFAULT_AD_CLIENT = "ca-pub-1885844100156878";
const DEFAULT_AD_SLOT = "5358639705";
const ADSENSE_HOST = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";

const isProduction = process.env.NODE_ENV === "production";
const scriptPromises = new Map();

function buildAdsenseSrc(client) {
  const adClient = client || DEFAULT_AD_CLIENT;
  return `${ADSENSE_HOST}?client=${adClient}`;
}

function loadAdsenseScript(client) {
  if (typeof window === "undefined" || !isProduction) {
    return Promise.resolve();
  }

  const src = buildAdsenseSrc(client);
  if (scriptPromises.has(src)) {
    return scriptPromises.get(src);
  }

  const promise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        resolve();
      } else {
        existingScript.addEventListener("load", resolve, { once: true });
        existingScript.addEventListener("error", reject, { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset.loaded = "false";

    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true }
    );
    script.addEventListener("error", reject, { once: true });

    document.head.appendChild(script);
  });

  scriptPromises.set(src, promise);
  return promise;
}

const GoogleAd = ({
  adClient = DEFAULT_AD_CLIENT,
  adSlot = DEFAULT_AD_SLOT,
  adFormat = "auto",
  layout = "",
  layoutKey = "",
  fullWidthResponsive = true,
  className = "",
  style = {},
  placeholder = "광고 자리 (개발 환경)",
  adTest = "",
  pageUrl = "",
}) => {
  const adRef = useRef(null);

  useEffect(() => {
    if (!isProduction) {
      return undefined;
    }

    let isCanceled = false;

    loadAdsenseScript(adClient)
      .then(() => {
        if (isCanceled || !adRef.current) {
          return;
        }

        try {
          window.adsbygoogle = window.adsbygoogle || [];
          window.adsbygoogle.push({});
        } catch (error) {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.error("Adsense push error", error);
          }
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("Adsense script load error", error);
        }
      });

    return () => {
      isCanceled = true;
    };
  }, [adClient, adSlot, adFormat, layout, layoutKey, fullWidthResponsive, adTest, pageUrl]);

  if (!isProduction) {
    return (
      <div
        data-testid="adsense-placeholder"
        className={`adsense-placeholder ${className}`.trim()}
        style={{
          display: "block",
          minHeight: "120px",
          border: "1px dashed #ccc",
          borderRadius: "4px",
          color: "#666",
          fontSize: "0.875rem",
          lineHeight: 1.6,
          textAlign: "center",
          padding: "16px",
          backgroundColor: "#fafafa",
          ...style,
        }}
      >
        {placeholder}
      </div>
    );
  }

  return (
    <ins
      className={`adsbygoogle ${className}`.trim()}
      style={{ display: "block", ...style }}
      data-ad-client={adClient}
      data-ad-slot={adSlot}
      data-ad-format={adFormat}
      data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
      data-ad-layout={layout || undefined}
      data-ad-layout-key={layoutKey || undefined}
      data-adtest={adTest || undefined}
      data-page-url={pageUrl || undefined}
      ref={adRef}
    />
  );
};

export default GoogleAd;
